/**
 * Topic service implementation
 *
 * Developed By : Travancore Analytics (2025)
 * Author: Akhil Soman
 * Created on: 04/03/2025
 * Project: Vitromed
 * Revision History:
 * SNo.   | Date        | Author        | Task                | Comments |
 * 1      04/03/2025    Akhil Soman       Creating new File
 */

import api from "./api.ts"; // Import the centralized Axios instance

class TopicService {
  /**
   * Create a new topic
   * @param url - API endpoint
   * @param data - Topic data to be created
   * @returns Promise with the response data
   */
  async createTopic(url: string, data: any) {
    try {
      const response = await api.post(url, data);
      if (response.status === 200 || response.status === 201) {
        return response.data; // Return the response data
      } else {
        throw new Error(`Unexpected status code: ${response.status}`);
      }
    } catch (error) {
      console.error("Error creating topic:", error);
      throw error;
    }
  }

  /**
   * Fetch all topics with pagination support
   * @param url - API endpoint
   * @param params - Pagination parameters (currentPage, perPage)
   * @returns Promise with the response data
   */
  async getAllTopics(
    url: string,
    params: { pageIndex: number; perSize: number }
  ) {
    try {
      const response = await api.get(url, {
        params: {
          page: params.pageIndex,
          perPage: params.perSize,
        },
      });
      if (response.status === 200) {
        return response.data; // Return the response data
      } else {
        throw new Error(`Unexpected status code: ${response.status}`);
      }
    } catch (error) {
      console.error("Error fetching topics:", error);
      throw error;
    }
  }

  /**
   * Fetch all topics without pagination
   * @param url - API endpoint
   * @returns Promise with the response data
   */
  async getAllTopicsBy(url: string) {
    try {
      const response = await api.get(url);
      if (response.status === 200) {
        return response.data?.topics || []; // Return the topics array or an empty array if not found
      } else {
        throw new Error(`Unexpected status code: ${response.status}`);
      }
    } catch (error) {
      console.error("Error fetching topics:", error);
      throw error;
    }
  }

  /**
   * Fetch all topics without pagination
   * @param url - API endpoint
   * @returns Promise with the response data
   */
  async getAllTopicsByIsForAssessment(url: string) {
    try {
      const response = await api.get(url, {
        params: {
          isAssessment: true,
        },
      });
      if (response.status === 200) {
        return response.data?.topics || []; // Return the topics array or an empty array if not found
      } else {
        throw new Error(`Unexpected status code: ${response.status}`);
      }
    } catch (error) {
      console.error("Error fetching topics:", error);
      throw error;
    }
  }

  /**
   * Fetch questions by topic ID
   * @param url - API endpoint
   * @param topicId - ID of the topic
   * @returns Promise with the response data
   */
  async getQuestionByTopicId(url: string, topicId: number) {
    try {
      const response = await api.get(url, {
        params: { TopicsID: topicId }, // Pass topicId as a query parameter
      });
      if (response.status === 200) {
        return response.data?.questions || []; // Return the questions array or an empty array if not found
      } else {
        throw new Error(`Unexpected status code: ${response.status}`);
      }
    } catch (error) {
      console.error("Error fetching questions by topic ID:", error);
      throw error;
    }
  }

  /**
   * Update an existing topic
   * @param url - API endpoint
   * @param data - Updated topic data
   * @returns Promise with the response data
   */
  async updateTopic(url: string, data: any) {
    try {
      const response = await api.put(url, data);
      if (response.status === 200) {
        return response.data; // Return the response data
      } else {
        throw new Error(`Unexpected status code: ${response.status}`);
      }
    } catch (error) {
      console.error("Error updating topic:", error);
      throw error;
    }
  }

  /**
   * Delete a topic
   * @param url - API endpoint
   * @param topicsId - ID of the topic to delete
   * @returns Promise with the response data
   */
  async deleteTopic(url: string, topicsId: number) {
    try {
      const response = await api.delete(url, {
        data: { TopicsID: topicsId }, // Send topicId in the request body
      });
      if (response.status === 200) {
        return response.data; // Return the response data
      } else {
        throw new Error(`Unexpected status code: ${response.status}`);
      }
    } catch (error) {
      console.error("Error deleting topic:", error);
      throw error;
    }
  }
}

const topicService = new TopicService();
export default topicService;
